import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { ACLCanType, ACLService } from '@delon/acl';
import { _HttpClient } from '@delon/theme';
import { finalize } from 'rxjs';
import { ReportingService } from 'src/app/core/util/reporting.service';
import BIRCasAccountableFormsJson from 'src/assets/data/bir-cas-accountable-forms.json';

@Component({
  selector: 'app-preview-doc',
  templateUrl: './preview-doc.component.html',
  styleUrls: ['./preview-doc.component.less'],
  standalone: false
})
export class ReportPreviewDocComponent implements OnChanges, OnInit {
  private _loadedFormatTypes = '';
  @Input() reportTypes: ReportTypeItem[] = [];
  @Input() docCode: string | undefined;
  @Input() einvoiceControl: number = -1;
  // @Input() options: any | undefined;
  @Input() filter: any | undefined;
  @Input() additionalData: any | undefined;
  @Input() noAcl: boolean = false;
  @Input() validate: ((filter: any, type: any) => Promise<boolean>) | undefined;
  @Input() disabled: boolean = false;

  private _loadedQueryParamsJson = '';

  reportFormats: any[] = [];

  refreshing = false;
  loading: boolean = false;
  bircaslist: any[] = [];
  // visibleReports: any[] = [];

  constructor(
    private acl: ACLService,
    private reporting: ReportingService,
    private http: _HttpClient,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.bircaslist = [...BIRCasAccountableFormsJson];
    this.loadReportFormats();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['reportTypes'] || changes['einvoiceControl']) {
      this.loadReportFormats();
    }
  }

  async previewFormat(reportFormat: any) {
    let valid = true;
    var type = reportFormat.reportType;
    if (this.validate) {
      await this.validate(this.filter, type)
        .then(res => {
          if (res === false) {
            valid = false;
            return;
          }
        })
        .catch(() => {
          valid = false;
          return;
        });
    }

    if (!valid) return;

    var filter = this.filter ?? {};
    Object.assign(filter, { selection: [this.docCode] });

    if (
      this.bircaslist.some((o: any) => o.reportType === type) &&
      !reportFormat.isPredefined
    ) {
      Object.assign(filter, {
        isReportClone: true,
        isCloneABIRCasRegistered: reportFormat.isBIRFormat
      });
    }

    if (type && this.docCode) {
      this.loading = true;

      this.reporting
        .preview(this.docCode, type, reportFormat.reportUrl, filter, true, this.additionalData)
        .then(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
        .catch(() => {
          this.loading = false;
          this.cdr.detectChanges();
        });
    }
  }

  loadReportFormats() {
    // Check if the paramsJson is the same as the last loaded paramsJson
    const paramsJson = JSON.stringify({
      reportTypes: this.reportTypes,
      einvoiceControl: this.einvoiceControl
    });

    // If the paramsJson is the same as the last loaded paramsJson, return
    if (paramsJson === this._loadedQueryParamsJson) return;

    this._loadedQueryParamsJson = paramsJson;

    if (this.reportTypes) {
      const reportTypesAllowed = this.noAcl
        ? this.reportTypes
        : this.reportTypes.filter(x =>
            x.acl ? this.acl.can(x.acl) : this.acl.can(`${x.reportType}.view`)
          );

      if (reportTypesAllowed?.length) {
        const formatTypesToLoad = reportTypesAllowed
          .map(x => x.reportType)
          .join(',');
        // if (formatTypesToLoad === this._loadedFormatTypes) return;

        this.reportFormats = [];
        this.refreshing = true;
        this.http
          .get(
            `${this.reporting.reportServerMirrorUrl}/api/ReportFiles/GetByType`,
            {
              reportType: formatTypesToLoad
            }
          )
          .subscribe({
            next: (res: any[]) => {
              this._loadedFormatTypes = formatTypesToLoad;

              // reorder default report
              if (this.isAllowedReportType(this._loadedFormatTypes)) {
                const transactionType = this.getReportType(
                  this._loadedFormatTypes
                );

                // Get the EInvDefault data for einvControl
                this.http
                  .get('api/TenantData/EInvDefault')
                  .pipe(
                    finalize(() => {
                      this.refreshing = false;
                      this.cdr.detectChanges();
                    })
                  )
                  .subscribe((eInvData: any[]) => {
                    // Reorder report formats based on einvControl
                    const defaultReport = eInvData ? this.reorderReportFormats(
                      eInvData,
                      this.einvoiceControl,
                      transactionType
                    ) : null;

                    if (defaultReport) {
                      // Move defaultReport to the start of res
                      this.reportFormats = [
                        ...res.filter(r => r.reportUrl === defaultReport),
                        ...res.filter(r => r.reportUrl !== defaultReport)
                      ];
                    }
                    else {
                      this.reportFormats = [...res];
                    }
                  });
              } else {
                // If not the stated report type
                this.reportFormats = [...res];
                this.refreshing = false;
                this.cdr.detectChanges();
              }
            },
            error: () => {
              this.reportFormats = [];
              this.refreshing = false;
              this.cdr.detectChanges();
            }
          });
      }
    }
  }

  private reorderReportFormats(
    eInvData: any[],
    einvControl: number,
    transactionType: string
  ) {
    const einvControlMap: { [key: number]: string } = {
      0: 'draft',
      1: 'portal',
      2: 'full',
      3: 'exempted'
    };

    const formatKey = einvControlMap[einvControl]; // Get the format key based on einvControl

    if (transactionType && formatKey) {
      const transaction = eInvData.find(
        t => t.transactionType === transactionType
      );
      return transaction ? transaction[formatKey] : null; // Returns the full repx string or null if not found
    }
    return null;
  }

  getReportType(reportTypes: string) {
    switch (reportTypes) {
      case 'sinv-voucher':
        return 'SINV';
      case 'sdn-voucher':
        return 'SDN';
      case 'scn-voucher':
        return 'SCN';
      case 'cs-voucher':
        return 'CS';
      case 'pinv-voucher':
        return 'PINV';
      case 'pdn-voucher':
        return 'PDN';
      case 'pcn-voucher':
        return 'PCN';
      case 'cp-voucher':
        return 'CP';
      default:
        return '';
    }
  }

  private isAllowedReportType(reportType: string): boolean {
    // List of allowed report types
    const allowedReportTypes = [
      'sinv-voucher',
      'sdn-voucher',
      'scn-voucher',
      'cs-voucher',
      'pinv-voucher',
      'pdn-voucher',
      'pcn-voucher',
      'cp-voucher'
    ];

    // Check if reportType is in the allowed list
    return allowedReportTypes.includes(reportType);
  }
}

export interface ReportTypeItem {
  reportType: string;
  acl?: ACLCanType;
}
